/**
 * City + service slug map
 * --------------------------------------------------------------------
 * Source of truth for every city page we publish: the city hubs (HUBS),
 * the city + service pages built on CityPageLayout from the city-pages CMS
 * (CMS_PAGES), the older catch-all city + service pages rendered by
 * CityServicePage from src/data/cityServicePages/* (PUBLISHED), and the
 * EN/FR slug pairs (so hreflang alternates and French links work even when
 * the FR URL uses a different word).
 *
 * Used by:
 *   - src/App.jsx (the CMS city + service routes come from CMS_PAGES)
 *   - src/data/serviceSlugsFr.js (localizePath: EN ↔ FR city links)
 *   - src/data/cityServicePages/index.js (registry aggregation)
 *   - src/components/CityServicePage.jsx (hreflang, internal links, redirects)
 *   - src/components/ServicePage.jsx, ServiceTemplate (by-city links)
 *   - scripts/city-routes.js → scripts/generate-sitemap.js (sitemap entries)
 *
 * Keep this file free of imports: the build scripts run it as is.
 */

/* ── City hubs ────────────────────────────────────────────────────── */
// The seven city landing pages (CityPages/*Page.jsx). Québec City's French
// hub is /fr/quebec (/fr/quebec-city 301s there in vercel.json).
export const HUBS = {
  montreal:         { en: "/montreal",       fr: "/fr/montreal",       label: { en: "Montreal",       fr: "Montréal" } },
  laval:            { en: "/laval",          fr: "/fr/laval",          label: { en: "Laval",          fr: "Laval" } },
  "quebec-city":    { en: "/quebec-city",    fr: "/fr/quebec",         label: { en: "Quebec City",    fr: "Québec" } },
  "trois-rivieres": { en: "/trois-rivieres", fr: "/fr/trois-rivieres", label: { en: "Trois-Rivières", fr: "Trois-Rivières" } },
  toronto:          { en: "/toronto",        fr: "/fr/toronto",        label: { en: "Toronto",        fr: "Toronto" } },
  ottawa:           { en: "/ottawa",         fr: "/fr/ottawa",         label: { en: "Ottawa",         fr: "Ottawa" } },
  "niagara-falls":  { en: "/niagara-falls",  fr: "/fr/niagara-falls",  label: { en: "Niagara Falls",  fr: "Niagara Falls" } },
};

// Province of each city: which guard licence applies (QC: BSP, ON: PSISA).
export const CITY_PROVINCE = {
  montreal: "QC", laval: "QC", "quebec-city": "QC", "trois-rivieres": "QC",
  toronto: "ON", ottawa: "ON", "niagara-falls": "ON", gta: "ON",
};

/* ── City definitions (catch-all URL segments) ────────────────────── */
// Each city has an EN url segment and a FR url segment. The FR segment
// for "quebec-city" intentionally drops the redundant "-city" so that
// French URLs read /fr/quebec/agence-de-securite (matches local search
// intent for "agence de sécurité Québec").
export const CITIES = {
  montreal:        { en: "montreal",       fr: "montreal",       label: { en: "Montreal",       fr: "Montréal" } },
  toronto:         { en: "toronto",        fr: "toronto",        label: { en: "Toronto",        fr: "Toronto" } },
  ottawa:          { en: "ottawa",         fr: "ottawa",         label: { en: "Ottawa",         fr: "Ottawa" } },
  "quebec-city":   { en: "quebec-city",    fr: "quebec",         label: { en: "Quebec City",    fr: "Québec" } },
  laval:           { en: "laval",          fr: "laval",          label: { en: "Laval",          fr: "Laval" } },
  "niagara-falls": { en: "niagara-falls",  fr: "niagara-falls",  label: { en: "Niagara Falls",  fr: "Niagara Falls" } },
  gta:             { en: "gta",            fr: null,             label: { en: "GTA",            fr: "GTA" } },
};

/* ── Service definitions ──────────────────────────────────────────── */
// A canonical service key (use an English token) maps to:
//   - an EN url slug
//   - an optional FR url slug (some services aren't published in FR)
//   - a display label in each language
export const SERVICES = {
  "security-company":          { en: "security-company",          fr: "compagnie-de-securite",     label: { en: "Security Company",            fr: "Compagnie de sécurité" } },
  "security-agency":           { en: "security-agency",           fr: "agence-de-securite",        label: { en: "Security Agency",             fr: "Agence de sécurité" } },
  "security-guard-services":   { en: "security-guard-services",   fr: "services-de-securite",      label: { en: "Security Guard Services",     fr: "Services de sécurité" } },
  "security-guards":           { en: "security-guards",           fr: "agent-de-securite",         label: { en: "Security Guards",             fr: "Agent de sécurité" } },
  "gardien-de-securite":       { en: null,                        fr: "gardien-de-securite",       label: { en: "Security Officer",            fr: "Gardien de sécurité" } },
  "event-security":            { en: "event-security",            fr: "securite-evenementielle",   label: { en: "Event Security",              fr: "Sécurité événementielle" } },
  "construction-site-security":{ en: "construction-site-security",fr: "securite-chantier",         label: { en: "Construction Site Security",  fr: "Sécurité de chantier" } },
  "fire-watch-security":       { en: "fire-watch-security",       fr: "surveillance-incendie",     label: { en: "Fire Watch Security",         fr: "Surveillance incendie" } },
  "mobile-patrol":             { en: "mobile-patrol",             fr: "patrouille-mobile",         label: { en: "Mobile Patrol",               fr: "Patrouille mobile" } },
  "mobile-security":           { en: "mobile-security",           fr: null,                        label: { en: "Mobile Security",             fr: "Sécurité mobile" } },
  "private-security":          { en: "private-security",          fr: "securite-privee",           label: { en: "Private Security",            fr: "Sécurité privée" } },
  "bodyguard":                 { en: "bodyguard",                 fr: "garde-du-corps",            label: { en: "Bodyguard",                   fr: "Garde du corps" } },
  "executive-protection":      { en: "executive-protection",      fr: null,                        label: { en: "Executive Protection",        fr: "Protection rapprochée" } },
  "commercial-security":       { en: "commercial-security",       fr: "securite-commerciale",      label: { en: "Commercial Security",         fr: "Sécurité commerciale" } },
  "business-security":         { en: "business-security",         fr: null,                        label: { en: "Business Security",           fr: "Sécurité d’entreprise" } },
  "residential-security":      { en: "residential-security",      fr: "securite-residentielle",    label: { en: "Residential Security",        fr: "Sécurité résidentielle" } },
  "concierge-security":        { en: "concierge-security",        fr: null,                        label: { en: "Concierge Security",          fr: "Sécurité conciergerie" } },
  "loss-prevention":           { en: "loss-prevention",           fr: null,                        label: { en: "Loss Prevention",             fr: "Prévention des pertes" } },
  "retail-security":           { en: "retail-security",           fr: null,                        label: { en: "Retail Security",             fr: "Sécurité commerce de détail" } },
  "condo-security":            { en: "condo-security",            fr: null,                        label: { en: "Condo Security",              fr: "Sécurité de copropriété" } },
  "hotel-security":            { en: "hotel-security",            fr: null,                        label: { en: "Hotel Security",              fr: "Sécurité hôtelière" } },
  "hospital-security":         { en: "hospital-security",         fr: null,                        label: { en: "Hospital Security",           fr: "Sécurité hospitalière" } },
  "mall-security":             { en: "mall-security",             fr: null,                        label: { en: "Mall Security",               fr: "Sécurité de centres commerciaux" } },
  "nightclub-security":        { en: "nightclub-security",        fr: null,                        label: { en: "Nightclub Security",          fr: "Sécurité de boîtes de nuit" } },
  "corporate-security":        { en: "corporate-security",        fr: null,                        label: { en: "Corporate Security",          fr: "Sécurité corporative" } },
  "wedding-security":          { en: "wedding-security",          fr: null,                        label: { en: "Wedding Security",            fr: "Sécurité de mariage" } },
};

/* ── CMS city + service pages ─────────────────────────────────────── */
// The city + service pages built on CityPageLayout (CityServicePages/*.jsx,
// content from the city-pages CMS: data/generated/cityPages.json, keyed
// "<city>--<service>"). Each has its own route (src/App.jsx builds them from
// this table); fr: null = no French page. A CMS page owns its city + service
// combo: the catch-all page for the same combo is not published (PUBLISHED
// below) and its old address redirects here (CityServicePage).
export const CMS_PAGES = {
  "montreal--security-guards":               { en: "/montreal/security-guards",               fr: "/fr/montreal/agents-de-securite",      label: { en: "Security Guards",            fr: "Agents de sécurité" } },
  "montreal--event-security":                { en: "/montreal/event-security",                fr: "/fr/montreal/securite-evenementielle", label: { en: "Event Security",             fr: "Sécurité événementielle" } },
  "montreal--construction-site-security":    { en: "/montreal/construction-site-security",    fr: "/fr/montreal/securite-chantier",       label: { en: "Construction Site Security", fr: "Sécurité de chantier" } },
  "montreal--mobile-patrol":                 { en: "/montreal/mobile-patrol",                 fr: "/fr/montreal/patrouille-mobile",       label: { en: "Mobile Patrol",              fr: "Patrouille mobile" } },
  "toronto--security-guards":                { en: "/toronto/security-guards",                fr: null,                                   label: { en: "Security Guards",            fr: "Agents de sécurité" } },
  "toronto--event-security":                 { en: "/toronto/event-security",                 fr: null,                                   label: { en: "Event Security",             fr: "Sécurité événementielle" } },
  "toronto--construction-site-security":     { en: "/toronto/construction-site-security",     fr: null,                                   label: { en: "Construction Site Security", fr: "Sécurité de chantier" } },
  "toronto--mobile-patrol":                  { en: "/toronto/mobile-patrol",                  fr: null,                                   label: { en: "Mobile Patrol",              fr: "Patrouille mobile" } },
  "ottawa--security-guards":                 { en: "/ottawa/security-guards",                 fr: null,                                   label: { en: "Security Guards",            fr: "Agents de sécurité" } },
  "ottawa--event-security":                  { en: "/ottawa/event-security",                  fr: null,                                   label: { en: "Event Security",             fr: "Sécurité événementielle" } },
  "ottawa--construction-site-security":      { en: "/ottawa/construction-site-security",      fr: null,                                   label: { en: "Construction Site Security", fr: "Sécurité de chantier" } },
  "ottawa--mobile-patrol":                   { en: "/ottawa/mobile-patrol",                   fr: null,                                   label: { en: "Mobile Patrol",              fr: "Patrouille mobile" } },
  "laval--security-guards":                  { en: "/laval/security-guards",                  fr: "/fr/laval/agent-de-securite",          label: { en: "Security Guards",            fr: "Agent de sécurité" } },
  "laval--event-security":                   { en: "/laval/event-security",                   fr: "/fr/laval/securite-evenementielle",    label: { en: "Event Security",             fr: "Sécurité événementielle" } },
  "quebec-city--security-guards":            { en: "/quebec-city/security-guards",            fr: "/fr/quebec/agent-de-securite",         label: { en: "Security Guards",            fr: "Agent de sécurité" } },
  "quebec-city--event-security":             { en: "/quebec-city/event-security",             fr: "/fr/quebec/securite-evenementielle",   label: { en: "Event Security",             fr: "Sécurité événementielle" } },
  "quebec-city--construction-site-security": { en: "/quebec-city/construction-site-security", fr: "/fr/quebec/securite-chantier",         label: { en: "Construction Site Security", fr: "Sécurité de chantier" } },
  "quebec-city--mobile-patrol":              { en: "/quebec-city/mobile-patrol",              fr: "/fr/quebec/patrouille-mobile",         label: { en: "Mobile Patrol",              fr: "Patrouille mobile" } },
  "niagara-falls--event-security":           { en: "/niagara-falls/event-security",           fr: null,                                   label: { en: "Event Security",             fr: "Sécurité événementielle" } },
  "niagara-falls--hospitality-security":     { en: "/niagara-falls/hospitality-security",     fr: null,                                   label: { en: "Hospitality Security",       fr: "Sécurité hôtelière" } },
};

/* ── The published catch-all combo matrix ─────────────────────────── */
// For each (lang, city), the service keys rendered by the catch-all template
// (CityServicePage, content in src/data/cityServicePages/<city>.<lang>.js).
// Anything not in this matrix isn't published — the catch-all route sends it
// to the CMS page that owns the combo (CMS_PAGES) or home. Combos owned by a
// CMS page are left out: Montréal/Toronto/Ottawa/Québec event, construction
// and mobile-patrol pages, the Laval and Niagara event pages, and the French
// /fr/montreal/agent-de-securite (now /fr/montreal/agents-de-securite).
export const PUBLISHED = {
  en: {
    montreal: [
      "security-company", "security-guard-services", "security-agency",
      "fire-watch-security", "private-security", "bodyguard",
      "commercial-security", "residential-security", "concierge-security",
      "loss-prevention", "retail-security", "condo-security",
    ],
    toronto: [
      "security-company", "security-guard-services", "security-agency",
      "fire-watch-security", "private-security", "bodyguard",
      "executive-protection", "commercial-security", "business-security",
      "condo-security", "concierge-security", "residential-security",
      "retail-security", "loss-prevention", "hotel-security",
      "hospital-security", "mall-security", "nightclub-security",
      "corporate-security", "wedding-security",
    ],
    // gta: no content yet (add src/data/cityServicePages/gta.en.js first)
    ottawa: [
      "security-company", "security-guard-services", "security-agency",
      "fire-watch-security", "private-security", "bodyguard",
      "executive-protection", "commercial-security", "condo-security",
      "residential-security", "retail-security", "concierge-security",
    ],
    "quebec-city": [
      "security-company", "security-guard-services", "security-agency",
      "private-security", "commercial-security", "residential-security",
    ],
    laval: [
      "security-company", "security-guard-services", "security-agency",
      "construction-site-security", "mobile-patrol",
    ],
    "niagara-falls": [
      "security-company", "construction-site-security",
    ],
  },
  fr: {
    montreal: [
      "security-agency",         // agence-de-securite
      "security-company",        // compagnie-de-securite
      "gardien-de-securite",     // gardien-de-securite
      "security-guard-services", // services-de-securite
      "private-security",        // securite-privee
      "bodyguard",               // garde-du-corps
      "commercial-security",     // securite-commerciale
      "residential-security",    // securite-residentielle
    ],
    "quebec-city": [
      "security-agency",
      "security-company",
      "gardien-de-securite",
      "security-guard-services",
      "private-security",
    ],
    laval: [
      "security-agency",
      "security-company",
      "security-guard-services",
      "construction-site-security",
      "mobile-patrol",
    ],
  },
};

/* ── Service hubs ─────────────────────────────────────────────────── */
// City + service key → the /services/:slug hub covering it (EN data key; the
// FR slug comes from serviceSlugsFr.js). A link to a city + service page that
// doesn't exist in a language falls back to this hub, then to the city hub.
export const SERVICE_HUBS = {
  "security-guards": "security-guards",
  "security-guard-services": "security-guards",
  "gardien-de-securite": "security-guards",
  "event-security": "event-security",
  "wedding-security": "event-security",
  "construction-site-security": "construction-site-security",
  "fire-watch-security": "fire-watch-security",
  "mobile-patrol": "patrol-services",
  "mobile-security": "patrol-services",
  "residential-security": "residential-security",
  "condo-security": "residential-security",
  "concierge-security": "residential-security",
  "commercial-security": "commercial-properties",
  "business-security": "commercial-properties",
  "corporate-security": "commercial-properties",
  "loss-prevention": "loss-prevention",
  "retail-security": "loss-prevention",
  "mall-security": "loss-prevention",
  "hospitality-security": "hospitality-security",
  "hotel-security": "hospitality-security",
  "nightclub-security": "hospitality-security",
  "bodyguard": "luxury-security",
  "executive-protection": "luxury-security",
};

/* ── Lookups ──────────────────────────────────────────────────────── */

/**
 * Given a URL slug + lang, find the canonical service key.
 * Returns null when the slug isn't a known service in that language.
 */
export function resolveServiceKeyFromSlug(lang, urlSlug) {
  for (const key of Object.keys(SERVICES)) {
    if (SERVICES[key][lang] === urlSlug) return key;
  }
  return null;
}

/**
 * Given a canonical service key + lang, return the URL slug for that lang
 * (or null if the service isn't published in that lang).
 */
export function urlSlugFor(lang, serviceKey) {
  const svc = SERVICES[serviceKey];
  return svc ? svc[lang] || null : null;
}

/** True when the catch-all template publishes this (lang, city, service) combo. */
export function isPublishedCombo(lang, cityKey, serviceKey) {
  return ((PUBLISHED[lang] || {})[cityKey] || []).includes(serviceKey);
}

/**
 * The page for a (city, service) combo in a language — the CMS page when one
 * owns the combo, else the published catch-all page — or null when there is
 * no such page in that language.
 */
export function cityServicePath(lang, cityKey, serviceKey) {
  const cms = CMS_PAGES[`${cityKey}--${serviceKey}`];
  if (cms && cms[lang]) return cms[lang];
  const city = CITIES[cityKey];
  const svc = SERVICES[serviceKey];
  if (!city || !svc || !city[lang] || !svc[lang] || !isPublishedCombo(lang, cityKey, serviceKey)) return null;
  return lang === "en" ? `/${city.en}/${svc.en}` : `/fr/${city.fr}/${svc.fr}`;
}

/**
 * Which city page an internal path points to (either language; ?query and
 * #hash ignored): { cityKey } for a city hub, { cityKey, serviceKey } for a
 * city + service page — also for wrong-language slugs such as
 * /fr/montreal/security-guards — or null when it isn't a city page.
 */
export function resolveCityPath(path) {
  const clean = String(path || "").split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  const isFr = /^\/fr(\/|$)/.test(clean);
  const segs = clean.replace(/^\/fr(?=\/|$)/, "").split("/").filter(Boolean);
  if (segs.length === 1) {
    const cityKey = Object.keys(HUBS).find((k) => k === segs[0] || HUBS[k].fr === `/fr/${segs[0]}`);
    return cityKey ? { cityKey } : null;
  }
  if (segs.length !== 2) return null;
  const tail = `/${segs[0]}/${segs[1]}`;
  for (const key of Object.keys(CMS_PAGES)) {
    const p = CMS_PAGES[key];
    if (p.en === tail || p.fr === `/fr${tail}`) {
      const [cityKey, serviceKey] = key.split("--");
      return { cityKey, serviceKey };
    }
  }
  const cityKey = Object.keys(CITIES).find((k) => CITIES[k].en === segs[0] || CITIES[k].fr === segs[0]);
  if (!cityKey) return null;
  for (const lang of isFr ? ["fr", "en"] : ["en", "fr"]) {
    const serviceKey = resolveServiceKeyFromSlug(lang, segs[1]);
    if (serviceKey) return { cityKey, serviceKey };
  }
  return null;
}

/**
 * Build the EN/FR hreflang pair for a given (city, service) combo.
 * Returns { en?, fr? } with absolute paths starting with "/".
 * Either side may be missing (the service may only be published in one lang).
 */
export function buildHreflangPair(citySlugCanonical, serviceKey) {
  const out = {};
  for (const lang of ["en", "fr"]) {
    const p = cityServicePath(lang, citySlugCanonical, serviceKey);
    if (p) out[lang] = p;
  }
  return out;
}

/**
 * The cities with a page for this service in a language, in CITIES order:
 * [{ cityKey, label, path }] (CMS pages included). For by-city link rows.
 */
export function citiesForService(lang, serviceKey) {
  return Object.keys(CITIES)
    .map((cityKey) => ({ cityKey, label: CITIES[cityKey].label[lang], path: cityServicePath(lang, cityKey, serviceKey) }))
    .filter((c) => c.path);
}

/**
 * Every city + service page as { lang, citySlug, serviceKey, urlPath, cms } —
 * the CMS pages plus the published catch-all combos. (Catch-all combos also
 * need their content in src/data/cityServicePages; scripts/city-routes.js
 * checks that for the sitemap.)
 */
export function enumerateCityServicePages() {
  const out = [];
  for (const lang of ["en", "fr"]) {
    for (const key of Object.keys(CMS_PAGES)) {
      const [citySlug, serviceKey] = key.split("--");
      if (CMS_PAGES[key][lang]) out.push({ lang, citySlug, serviceKey, urlPath: CMS_PAGES[key][lang], cms: true });
    }
  }
  const seen = new Set(out.map((p) => p.urlPath));
  for (const c of enumerateCombos()) {
    if (!seen.has(c.urlPath)) out.push({ ...c, cms: false });
  }
  return out;
}

/**
 * Enumerate every published catch-all combo as { lang, citySlug, serviceKey, urlPath }.
 */
export function enumerateCombos() {
  const out = [];
  for (const lang of ["en", "fr"]) {
    for (const cityKey of Object.keys(PUBLISHED[lang] || {})) {
      for (const serviceKey of PUBLISHED[lang][cityKey]) {
        const city = CITIES[cityKey];
        const svc = SERVICES[serviceKey];
        if (!city || !svc || !svc[lang] || !city[lang]) continue;
        const urlPath = lang === "en"
          ? `/${city.en}/${svc.en}`
          : `/fr/${city.fr}/${svc.fr}`;
        out.push({ lang, citySlug: cityKey, serviceKey, urlPath });
      }
    }
  }
  return out;
}
